import { ContentModelId } from '../types/ContentModelId'
import type { Entry } from '@b2ag/contentful'
import type { PartnerId } from '../types/PartnerId'

import { isNonNullableEntryDTO } from './non-nullable-entry-dto'
import { isNotUndefined } from './non-nullable'

import { mapBannerFromApi } from '../contexts/banner/infrastructure/repository/mapper/banner.mapper'
import { mapCardFromApi } from '../contexts/card/infrastructure/repository/mapper/card.mapper'
import { mapCardsGridFromApi } from '../contexts/cards-grid/infrastructure/repository/mapper/cards-grid.mapper'
import { mapCarouselContentFromApi } from '../contexts/carousel-content/infrastructure/repository/mapper/carousel-content.mapper'
import { mapCommonItemFromApi } from '../contexts/common-item/infrastructure/repository/mapper/common-item.mapper'
import { mapContentMediaFromApi } from '../contexts/content-media/infrastructure/repository/mapper/content-media.mapper'
import { mapContentPageFromApi } from '../contexts/content-page/infrastructure/repository/mapper/content-page.mapper'
import { mapContentsCarouselFromApi } from '../contexts/contents-carousel/infrastructure/repository/mapper/contents-carousel.mapper'
import { mapColumnContentFromApi } from '../contexts/column-content/infrastructure/repository/mapper/column-content.mapper'
import { mapFaqBlockFromApi } from '../contexts/faq-block/infrastructure/repository/mapper/faq-block.mapper'
import { mapFaqListFromApi } from '../contexts/faq-list/infrastructure/repository/mapper/faq-list.mapper'
import { mapImageGridFromApi } from '../contexts/image-grid/infrastructure/repository/mapper/image-grid.mapper'
import { mapListItemsFromApi } from '../contexts/list-items/infrastructure/repository/mapper/list-items.mapper'
import { mapMainBannerFromApi } from '../contexts/main-banner/infrastructure/repository/mapper/main-banner.mapper'
import { mapNewsletterFromApi } from '../contexts/newsletter/infrastructure/repository/mapper/newsletter.mapper'
import { mapRichLinkFromApi } from '../contexts/rich-link/infrastructure/repository/mapper/rich-link.mapper'
import { mapStepperFromApi } from '../contexts/stepper/infrastructure/repository/mapper/stepper.mapper'
import { mapThemeInsertFromApi } from '../contexts/theme-insert/infrastructure/repository/mapper/theme-insert.mapper'
import { mapUniverseGeneralInformationFromApi } from '../contexts/universe-general-information/infrastructure/repository/mapper/universe-general-information.mapper'
import { mapUniversePageFromApi } from '../contexts/universe-page/infrastructure/repository/mapper/universe-page.mapper'

type MapperMethods =
  | typeof mapBannerFromApi
  | typeof mapCardFromApi
  | typeof mapCardsGridFromApi
  | typeof mapCarouselContentFromApi
  | typeof mapColumnContentFromApi
  | typeof mapCommonItemFromApi
  | typeof mapContentMediaFromApi
  | typeof mapContentPageFromApi
  | typeof mapContentsCarouselFromApi
  | typeof mapFaqBlockFromApi
  | typeof mapFaqListFromApi
  | typeof mapImageGridFromApi
  | typeof mapListItemsFromApi
  | typeof mapMainBannerFromApi
  | typeof mapNewsletterFromApi
  | typeof mapRichLinkFromApi
  | typeof mapStepperFromApi
  | typeof mapThemeInsertFromApi
  | typeof mapUniverseGeneralInformationFromApi
  | typeof mapUniversePageFromApi

type MapperMethodsReturnType = ReturnType<MapperMethods>

type MapperList = Extract<
  ContentModelId,
  | ContentModelId.BANNER
  | ContentModelId.CARD
  | ContentModelId.CARDS_GRID
  | ContentModelId.CAROUSEL_CONTENT
  | ContentModelId.COLUMN_CONTENT
  | ContentModelId.COMMON_ITEM
  | ContentModelId.CONTENT_MEDIA
  | ContentModelId.CONTENT_PAGE
  | ContentModelId.CONTENTS_CAROUSEL
  | ContentModelId.FAQ_BLOCK
  | ContentModelId.FAQ_LIST
  | ContentModelId.IMAGE_GRID
  | ContentModelId.LIST_ITEMS
  | ContentModelId.MAIN_BANNER
  | ContentModelId.NEWSLETTER
  | ContentModelId.RICH_LINK
  | ContentModelId.STEPPER
  | ContentModelId.THEME_INSERT
  | ContentModelId.UNIVERSE_GENERAL_INFORMATION
  | ContentModelId.UNIVERSE_PAGE
>

export const mapEntryDTOList = <K extends MapperMethodsReturnType>(
  entries?: Array<Entry | undefined>,
  partnerId?: PartnerId,
): Array<K> | undefined => {
  if (!entries) {
    return undefined
  }

  const MAPPERS_LIST: Record<MapperList, MapperMethods> = {
    [ContentModelId.BANNER]: mapBannerFromApi,
    [ContentModelId.CARD]: mapCardFromApi,
    [ContentModelId.CARDS_GRID]: mapCardsGridFromApi,
    [ContentModelId.CAROUSEL_CONTENT]: mapCarouselContentFromApi,
    [ContentModelId.COLUMN_CONTENT]: mapColumnContentFromApi,
    [ContentModelId.COMMON_ITEM]: mapCommonItemFromApi,
    [ContentModelId.CONTENT_MEDIA]: mapContentMediaFromApi,
    [ContentModelId.CONTENT_PAGE]: mapContentPageFromApi,
    [ContentModelId.CONTENTS_CAROUSEL]: mapContentsCarouselFromApi,
    [ContentModelId.FAQ_BLOCK]: mapFaqBlockFromApi,
    [ContentModelId.FAQ_LIST]: mapFaqListFromApi,
    [ContentModelId.IMAGE_GRID]: mapImageGridFromApi,
    [ContentModelId.LIST_ITEMS]: mapListItemsFromApi,
    [ContentModelId.MAIN_BANNER]: mapMainBannerFromApi,
    [ContentModelId.NEWSLETTER]: mapNewsletterFromApi,
    [ContentModelId.RICH_LINK]: mapRichLinkFromApi,
    [ContentModelId.STEPPER]: mapStepperFromApi,
    [ContentModelId.THEME_INSERT]: mapThemeInsertFromApi,
    [ContentModelId.UNIVERSE_GENERAL_INFORMATION]: mapUniverseGeneralInformationFromApi,
    [ContentModelId.UNIVERSE_PAGE]: mapUniversePageFromApi,
  }

  return entries
    .filter(isNonNullableEntryDTO)
    .map((entry) => {
      const contentModel = entry.sys.contentType.sys.id as ContentModelId
      return MAPPERS_LIST[contentModel]?.apply(this, [entry, partnerId])
    })
    .filter(isNotUndefined)
}
